import { Router } from '@angular/router';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { AuthApi } from '@core/auth/services/auth-api';
import { LoggerService } from '@core/log/services/logger';
import { AppState } from '@core/state/services/app-state';
import { Nullable } from '@shared/models/types';
import { setToStorage } from '@shared/utility/session-local-storage';
import { StorageKeysEnum } from '@shared/utility/storage-keys';
import { getTimezone } from '@core/formatting/formatting-config';

/**
 * Codice di stato con cui l'interceptor fa fallire ogni richiesta mentre la sessione risulta scaduta. Non
 * appartiene a `HttpStatusCode`: nasce qui e non viaggia sulla rete, quindi chi lo riconosce lo importa da
 * questo file.
 */
export const SESSION_EXPIRED_HTTP_STATUS = 440;

export const BaseHttpInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthApi);
  const router = inject(Router);
  const logger = inject(LoggerService);

  const sessionExpired = inject(AppState).sessionExpired();

  const sessionExpiredError = throwError(() => {
    return new HttpErrorResponse({
      error: 'Session expired',
      status: SESSION_EXPIRED_HTTP_STATUS,
      statusText: 'Session expired'
    });
  });

  if (sessionExpired) {
    return sessionExpiredError;
  }

  const clonedReq = req.clone({
    setHeaders: {
      'X-TAG-TZ': getTimezone()
    }
  });

  return next(clonedReq).pipe(
    tap(() => {
      const embParam = router.parseUrl(router.url).queryParams['embedded'] as Nullable<string>;
      setToStorage('sessionStorage', StorageKeysEnum.embedded, embParam === 'true');
    }),
    catchError((err: HttpErrorResponse) => {
      const isUnauthorized = err.status === HttpStatusCode.Unauthorized;
      const isLoginRequest = req.url.includes('login');

      if (isLoginRequest) {
        if (isUnauthorized || err.status === HttpStatusCode.NotFound) {
          auth.logout(false);
          return throwError(() => err);
        } else if (err.status === HttpStatusCode.UnavailableForLegalReasons) {
          logger.warn('Cambio password necessario');
          router.navigate(['reset-password'], { state: { isLoginError: true } });
          return throwError(() => err);
        }
      } else if (isUnauthorized) {
        return sessionExpiredError;
      }

      return throwError(() => err);
    })
  );
};
